import {
   MIN_EXAM_ANSWERS,
   cloneLocalizedAnswers,
   normalizeExamQuestionRecord,
   parseJsonArraySafe,
   stringifyLocalizedAnswer,
   toPositiveInt,
} from "./examQuestionFormat";
import {
   extractTicketNumber,
   resolvePracticeTicketNumber,
} from "./examTicketsOfficialImport";
import { orderQuestionsByIds } from "./theoryOfficialTicketsImport";

export const AUTO_TEST_AB_RULE =
   "Sursă oficială auto_test_AB.json. Biletele P1–P24 după nume, întrebările după poziție în bilet. correct_idx din fișier (0-based) se salvează direct în DB — Practice citește index 0-based. Imaginile site-ului rămân neschimbate.";

const MAX_PRACTICE_TICKETS = 24;

function isObject(value) {
   return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function safeList(value) {
   return Array.isArray(value) ? value : [];
}

function firstNonEmpty(...values) {
   for (const value of values) {
      if (value == null) continue;
      const text = String(value).trim();
      if (text) return text;
   }
   return "";
}

function normalizeLooseKey(value) {
   return String(value ?? "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
}

export function normalizeBNumber(value) {
   if (value == null || value === "") return null;

   const text = String(value).trim().toUpperCase();
   const match = text.match(/^B?(\d+)$/);
   if (match?.[1]) return toPositiveInt(match[1]);

   return toPositiveInt(value);
}

export function formatBKey(bNumber) {
   const n = toPositiveInt(bNumber);
   return n ? `B${n}` : "";
}

export function extractBNumberFromQuestion(question) {
   if (!question) return null;

   const direct = normalizeBNumber(
      question?.b ??
         question?.bKey ??
         question?.number ??
         question?.qid ??
         question?.officialQid,
   );
   if (direct) return direct;

   const sources = [
      question?.textRo,
      question?.textRu,
      question?.text,
      question?.question,
   ];

   for (const source of sources) {
      if (typeof source === "string") {
         const match = source.match(/\bB(\d+)\b/i);
         if (match?.[1]) return toPositiveInt(match[1]);
      }

      if (isObject(source)) {
         const nested = firstNonEmpty(source?.ro, source?.ru);
         const match = nested.match(/\bB(\d+)\b/i);
         if (match?.[1]) return toPositiveInt(match[1]);
      }
   }

   return null;
}

function resolveOfficialTicketNo(raw) {
   const range = readPracticeTicketIdRange();
   const rawTicket = toPositiveInt(raw?.ticket ?? raw?.ticketNo ?? raw?.bilet);
   if (!rawTicket) return null;

   if (rawTicket >= 1 && rawTicket <= MAX_PRACTICE_TICKETS) {
      return rawTicket;
   }

   if (rawTicket >= range.start && rawTicket <= range.end) {
      return rawTicket - range.start + 1;
   }

   return null;
}

function readPracticeTicketIdRange() {
   const start = Number(
      process.env.REACT_APP_TICKETS_START ||
         process.env.VITE_TICKETS_START ||
         246,
   );
   const count = Number(
      process.env.REACT_APP_TICKETS_COUNT ||
         process.env.VITE_TICKETS_COUNT ||
         24,
   );
   const safeCount = Number.isInteger(count) && count > 0 ? count : 24;
   const safeStart = Number.isInteger(start) && start > 0 ? start : 246;

   return {
      start: safeStart,
      end: safeStart + safeCount - 1,
      count: safeCount,
   };
}

function readTicketQuestionIds(ticketRaw, normalizedQuestions = []) {
   const source =
      ticketRaw?.data && typeof ticketRaw.data === "object"
         ? ticketRaw.data
         : ticketRaw;
   const parsedIds = parseJsonArraySafe(
      source?.questionIds,
      safeList(source?.questionIds),
   );
   const ids = parsedIds
      .map((id) => Number(id))
      .filter((id) => Number.isInteger(id) && id > 0);

   if (ids.length) return ids;

   return safeList(normalizedQuestions)
      .map((question) => question?.id)
      .filter((id) => Number.isInteger(Number(id)) && Number(id) > 0);
}

function flattenOfficialItems(parsed) {
   if (Array.isArray(parsed)) return parsed;

   if (!isObject(parsed)) return [];

   const listKeys = ["items", "questions", "data", "entries", "rows"];
   for (const key of listKeys) {
      if (Array.isArray(parsed?.[key])) return parsed[key];
   }

   if (Array.isArray(parsed?.tickets)) {
      return parsed.tickets.flatMap((ticket) => {
         const ticketNo =
            resolveOfficialTicketNo(ticket) ||
            resolvePracticeTicketNumber(
               {
                  name: ticket?.ticketName ?? ticket?.name,
                  id: ticket?.ticketId ?? ticket?.id,
               },
               ticket?.ticketId ?? ticket?.id,
            ) ||
            toPositiveInt(ticket?.ticket ?? ticket?.ticketNo ?? ticket?.bilet);
         return safeList(ticket?.questions).map((question) => ({
            ...(isObject(question) ? question : {}),
            ticket: question?.ticket ?? ticketNo,
         }));
      });
   }

   if (isObject(parsed?.tickets)) {
      return Object.entries(parsed.tickets).flatMap(([code, ticket]) => {
         const ticketNo =
            toPositiveInt(ticket?.ticket ?? ticket?.ticketNo) ||
            extractTicketNumber(code);
         return safeList(ticket?.questions).map((question) => ({
            ...(isObject(question) ? question : {}),
            ticket: question?.ticket ?? ticketNo,
         }));
      });
   }

   return [];
}

function readLangBlock(raw, lang) {
   const block = raw?.[lang];
   if (!isObject(block)) {
      return {
         text: "",
         answers: [],
         correctIdx: null,
         hint: "",
      };
   }

   const answers = safeList(block?.answers)
      .map((answer) => {
         if (typeof answer === "string") return answer.trim();
         if (isObject(answer)) {
            return firstNonEmpty(answer?.ro, answer?.ru, answer?.text);
         }
         return String(answer ?? "").trim();
      })
      .filter(Boolean);

   let correctIdx = block?.correct_idx;
   if (!Number.isInteger(correctIdx)) {
      correctIdx = block?.correctIndex;
   }
   if (!Number.isInteger(correctIdx)) {
      correctIdx = null;
   }

   return {
      text: firstNonEmpty(block?.text, block?.question),
      answers,
      correctIdx,
      hint: firstNonEmpty(block?.hint, block?.explanation, block?.officialHint),
   };
}

function normalizeOfficialItem(raw, index = 0) {
   if (!isObject(raw)) {
      throw new Error(`Item #${index + 1} invalid.`);
   }

   const ticketNo = resolveOfficialTicketNo(raw);
   if (!ticketNo || ticketNo < 1 || ticketNo > MAX_PRACTICE_TICKETS) {
      throw new Error(
         `Item #${index + 1}: bilet invalid (${raw?.ticket ?? "?"}). Acceptat: P1–P${MAX_PRACTICE_TICKETS} sau id DB ${readPracticeTicketIdRange().start}–${readPracticeTicketIdRange().end}.`,
      );
   }

   const bNumber = normalizeBNumber(raw?.number ?? raw?.b ?? raw?.B);
   const bKey = bNumber ? formatBKey(bNumber) : "";

   const ro = readLangBlock(raw, "ro");
   const ru = readLangBlock(raw, "ru");

   if (!ro.text && !ru.text) {
      throw new Error(
         `P${ticketNo} item #${index + 1}: lipsește textul întrebării.`,
      );
   }

   if (!ro.answers.length && !ru.answers.length) {
      throw new Error(`P${ticketNo} item #${index + 1}: lipsesc răspunsurile.`);
   }

   const answerCount = Math.max(ro.answers.length, ru.answers.length);
   const answers = Array.from({ length: answerCount }, (_, answerIndex) => ({
      ro: ro.answers[answerIndex] || ru.answers[answerIndex] || "",
      ru: ru.answers[answerIndex] || ro.answers[answerIndex] || "",
   }));

   if (answers.length < 2) {
      throw new Error(
         `P${ticketNo} item #${index + 1}: minimum 2 răspunsuri necesare.`,
      );
   }

   let correctIdx = Number.isInteger(ro.correctIdx) ? ro.correctIdx : null;
   if (!Number.isInteger(correctIdx) && Number.isInteger(ru.correctIdx)) {
      correctIdx = ru.correctIdx;
   }
   if (
      !Number.isInteger(correctIdx) ||
      correctIdx < 0 ||
      correctIdx >= answers.length
   ) {
      correctIdx = 0;
   }

   const correctAnswer = correctIdx;

   return {
      ticketNo,
      ticketCode: `P${ticketNo}`,
      bNumber,
      bKey: bKey || null,
      label: bKey || `P${ticketNo}`,
      ro,
      ru,
      answers,
      correctIdx,
      correctAnswer,
      draft: {
         textRo: ro.text,
         textRu: ru.text,
         hintRo: ro.hint,
         hintRu: ru.hint,
         answers,
         correctAnswer,
         categoryId: null,
         image: "",
      },
   };
}

function assignPositionInTicket(items = []) {
   const groups = new Map();

   for (const item of safeList(items)) {
      const ticketNo = toPositiveInt(item?.ticketNo);
      if (!ticketNo) continue;
      if (!groups.has(ticketNo)) groups.set(ticketNo, []);
      groups.get(ticketNo).push(item);
   }

   const positioned = [];

   for (const ticketNo of Array.from(groups.keys()).sort((a, b) => a - b)) {
      const list = groups.get(ticketNo) || [];
      list.forEach((item, index) => {
         positioned.push({
            ...item,
            positionInTicket: index + 1,
            label: item.bKey || `P${ticketNo} · #${index + 1}`,
         });
      });
   }

   return positioned;
}

export function isAutoTestAbImport(parsed) {
   const items = flattenOfficialItems(parsed);
   if (!items.length) return false;

   const first = items[0];
   return (
      isObject(first?.ro) &&
      isObject(first?.ru) &&
      (Array.isArray(first.ro?.answers) || first.ro?.text != null)
   );
}

export function parseAutoTestAbImport(parsed) {
   const errors = [];
   const type = String(parsed?.type ?? "auto_test_AB").trim();
   const rule = String(parsed?.rule ?? parsed?.description ?? AUTO_TEST_AB_RULE).trim();

   const rawItems = flattenOfficialItems(parsed)
      .map((item, index) => {
         try {
            return normalizeOfficialItem(item, index);
         } catch (error) {
            errors.push(error?.message || `Item invalid la index ${index + 1}.`);
            return null;
         }
      })
      .filter(Boolean);

   const items = assignPositionInTicket(rawItems);

   if (!items.length) {
      errors.push(
         "Fișier invalid. Aștept items cu ticket (1–24), ro/ru{text,answers,correct_idx,hint}.",
      );
   }

   const keys = new Set();
   for (const item of items) {
      const key = `${item.ticketNo}:${item.positionInTicket}`;
      if (keys.has(key)) {
         errors.push(
            `Duplicat în fișier: P${item.ticketNo} poziția ${item.positionInTicket}.`,
         );
      }
      keys.add(key);
   }

   return {
      type,
      rule,
      items,
      errors,
      summary: {
         officialTotal: items.length,
         ticketsTotal: new Set(items.map((item) => item.ticketNo)).size,
      },
   };
}

function parseHintFromRaw(raw) {
   if (!isObject(raw)) {
      return { hintRo: "", hintRu: "" };
   }

   const localized = raw?.hint ?? raw?.explanation ?? raw?.hintText;
   if (typeof localized === "string") {
      try {
         const parsed = JSON.parse(localized);
         if (isObject(parsed)) {
            return {
               hintRo: firstNonEmpty(parsed?.ro, parsed?.RO),
               hintRu: firstNonEmpty(parsed?.ru, parsed?.RU),
            };
         }
      } catch {
         return { hintRo: localized.trim(), hintRu: "" };
      }
   }

   if (isObject(localized)) {
      return {
         hintRo: firstNonEmpty(localized?.ro, localized?.RO),
         hintRu: firstNonEmpty(localized?.ru, localized?.RU),
      };
   }

   return {
      hintRo: firstNonEmpty(raw?.hintRo, raw?.hint_ro),
      hintRu: firstNonEmpty(raw?.hintRu, raw?.hint_ru),
   };
}

export function normalizeSiteQuestionRecord(raw, index = 0) {
   const base = normalizeExamQuestionRecord(raw, index, { ticketType: "PRACTICE" });
   if (!base) return null;

   const hints = parseHintFromRaw(raw);
   return {
      ...base,
      // de la 0, ca pe server și ca correct_idx din fișier (comparația din dry-run)
      correctAnswer: base.correctAnswer - 1,
      hintRo: hints.hintRo,
      hintRu: hints.hintRu,
      bNumber: extractBNumberFromQuestion({
         ...base,
         text: raw?.text,
         question: raw?.question,
      }),
   };
}

function comparableShape(question) {
   return {
      textRo: String(question?.textRo ?? "").trim(),
      textRu: String(question?.textRu ?? "").trim(),
      hintRo: String(question?.hintRo ?? "").trim(),
      hintRu: String(question?.hintRu ?? "").trim(),
      correctAnswer: Number(question?.correctAnswer ?? 0),
      answers: safeList(question?.answers).map((answer) => ({
         ro: String(answer?.ro ?? "").trim(),
         ru: String(answer?.ru ?? "").trim(),
      })),
   };
}

export function questionNeedsUpdate(current, officialDraft, preservedImage = "") {
   const next = {
      ...officialDraft,
      image: preservedImage,
   };
   return (
      JSON.stringify(comparableShape(current)) !==
      JSON.stringify(comparableShape(next))
   );
}

export function buildAutoTestAbPutPayload(officialItem, existingQuestion) {
   const preservedImage = String(existingQuestion?.image ?? "").trim();
   const categoryId = existingQuestion?.categoryId ?? null;

   const draft = {
      ...officialItem.draft,
      image: preservedImage,
      categoryId,
   };

   const textRo = String(draft?.textRo ?? "").trim();
   const textRu = String(draft?.textRu ?? "").trim();

   if (!textRo && !textRu) {
      throw new Error("Întrebarea trebuie să aibă text în cel puțin o limbă.");
   }

   const answers = cloneLocalizedAnswers(draft?.answers).map((answer) => ({
      ro: String(answer?.ro ?? "").trim(),
      ru: String(answer?.ru ?? "").trim(),
   }));

   if (answers.length < MIN_EXAM_ANSWERS) {
      throw new Error(`Sunt necesare minimum ${MIN_EXAM_ANSWERS} răspunsuri.`);
   }

   if (answers.some((answer) => !answer.ro && !answer.ru)) {
      throw new Error("Toate răspunsurile trebuie completate înainte de salvare.");
   }

   const correctAnswer = Number(draft?.correctAnswer);
   if (
      !Number.isInteger(correctAnswer) ||
      correctAnswer < 0 ||
      correctAnswer >= answers.length
   ) {
      throw new Error("Răspunsul corect este invalid.");
   }

   const payload = {
      text: JSON.stringify({
         ro: textRo,
         ru: textRu,
      }),
      answers: answers.map((answer) => stringifyLocalizedAnswer(answer)),
      correctAnswer,
      categoryId,
      image: preservedImage,
   };

   const hintRo = String(draft?.hintRo ?? "").trim();
   const hintRu = String(draft?.hintRu ?? "").trim();
   if (hintRo || hintRu) {
      payload.hint = JSON.stringify({
         ro: hintRo || hintRu,
         ru: hintRu || hintRo,
      });
   }

   return payload;
}

export async function loadPracticeSiteSnapshot({
   loadTickets,
   loadTicketById,
   loadTicketQuestions,
   maxTickets = MAX_PRACTICE_TICKETS,
} = {}) {
   if (
      typeof loadTickets !== "function" ||
      typeof loadTicketById !== "function" ||
      typeof loadTicketQuestions !== "function"
   ) {
      throw new Error("loadPracticeSiteSnapshot: lipsesc funcțiile de încărcare.");
   }

   const tickets = safeList(await loadTickets())
      .map((ticket) => {
         const ticketNo = resolvePracticeTicketNumber(ticket);
         return {
            ...ticket,
            ticketNo,
            ticketCode: ticketNo ? `P${ticketNo}` : "",
         };
      })
      .filter((ticket) => ticket.ticketNo && ticket.ticketNo <= maxTickets)
      .sort((left, right) => left.ticketNo - right.ticketNo);

   const entries = [];

   for (const ticket of tickets) {
      const ticketRaw = await loadTicketById(ticket.id);
      const questionsResponse = await loadTicketQuestions(ticket.id);
      const normalized = safeList(questionsResponse?.items)
         .map((row, index) => normalizeSiteQuestionRecord(row, index))
         .filter(Boolean);

      const questionIds = readTicketQuestionIds(ticketRaw, normalized);

      const ordered = orderQuestionsByIds(normalized, questionIds).map(
         (question, index) => ({
            ...question,
            ticketNo: ticket.ticketNo,
            ticketId: ticket.id,
            ticketCode: ticket.ticketCode,
            positionInTicket: index + 1,
         }),
      );

      entries.push(...ordered);
   }

   return entries;
}

function readExportQuestions(bundle) {
   const raw = bundle?.questions;
   if (Array.isArray(raw)) return raw;
   if (Array.isArray(raw?.questions)) return raw.questions;
   if (Array.isArray(raw?.data)) return raw.data;
   if (Array.isArray(raw?.data?.questions)) return raw.data.questions;
   return [];
}

export function buildSiteSnapshotFromExport(exportDoc) {
   const entries = [];

   for (const bundle of safeList(exportDoc?.tickets)) {
      const ticketId = toPositiveInt(bundle?.ticketId ?? bundle?.listItem?.id);
      const ticketNo = resolvePracticeTicketNumber(
         {
            name: bundle?.ticketName ?? bundle?.listItem?.name,
            id: ticketId,
         },
         ticketId,
      );
      if (!ticketNo || ticketNo > MAX_PRACTICE_TICKETS) continue;

      const ticketCode = `P${ticketNo}`;
      const rawQuestions = readExportQuestions(bundle);
      const normalized = rawQuestions
         .map((row, index) => normalizeSiteQuestionRecord(row, index))
         .filter(Boolean);
      const questionIds = readTicketQuestionIds(
         bundle?.ticket ?? bundle?.listItem,
         normalized,
      );
      const ordered = orderQuestionsByIds(normalized, questionIds).map(
         (question, index) => ({
            ...question,
            ticketNo,
            ticketId,
            ticketCode,
            positionInTicket: index + 1,
         }),
      );

      entries.push(...ordered);
   }

   return entries;
}

function buildSiteIndex(siteEntries = []) {
   const byTicketOrdered = new Map();
   const all = [];

   for (const entry of safeList(siteEntries)) {
      all.push(entry);
      const ticketNo = toPositiveInt(entry?.ticketNo);
      if (!ticketNo) continue;
      if (!byTicketOrdered.has(ticketNo)) byTicketOrdered.set(ticketNo, []);
      byTicketOrdered.get(ticketNo).push(entry);
   }

   for (const [ticketNo, list] of byTicketOrdered.entries()) {
      byTicketOrdered.set(
         ticketNo,
         [...list].sort(
            (left, right) =>
               Number(left?.positionInTicket ?? 0) -
               Number(right?.positionInTicket ?? 0),
         ),
      );
   }

   return { byTicketOrdered, all };
}

export function runAutoTestAbCorrection({
   bundle,
   siteEntries = [],
   apply = false,
} = {}) {
   const officialItems = safeList(bundle?.items);
   const { byTicketOrdered, all: siteAll } = buildSiteIndex(siteEntries);

   const report = {
      officialTotal: officialItems.length,
      foundInSite: 0,
      updated: 0,
      wouldUpdate: 0,
      missingInSite: 0,
      notInOfficialFile: 0,
      answerCountMismatch: 0,
      skipped: 0,
      unchanged: 0,
   };

   const rows = [];
   const updates = [];
   const matchedQuestionIds = new Set();

   for (const official of officialItems) {
      const ticketQuestions = byTicketOrdered.get(official.ticketNo) || [];
      const siteQuestion =
         ticketQuestions[Number(official.positionInTicket) - 1] || null;

      if (!siteQuestion) {
         report.missingInSite += 1;
         rows.push({
            status: "missing_in_site",
            ticketCode: official.ticketCode,
            bKey: official.label,
            positionInTicket: official.positionInTicket,
            message: `Nu există întrebarea la poziția ${official.positionInTicket} în ${official.ticketCode}.`,
         });
         continue;
      }

      matchedQuestionIds.add(Number(siteQuestion.id));

      report.foundInSite += 1;

      const answerCountMismatch =
         safeList(siteQuestion?.answers).length !== official.answers.length;
      if (answerCountMismatch) {
         report.answerCountMismatch += 1;
      }

      const payload = buildAutoTestAbPutPayload(official, siteQuestion);
      const needsUpdate = questionNeedsUpdate(
         siteQuestion,
         official.draft,
         siteQuestion.image,
      );

      if (!needsUpdate) {
         report.unchanged += 1;
         matchedQuestionIds.add(Number(siteQuestion.id));
         rows.push({
            status: "unchanged",
            ticketCode: official.ticketCode,
            bKey: official.label,
            positionInTicket: official.positionInTicket,
            questionId: siteQuestion.id,
            message: answerCountMismatch
               ? "Fără diferențe text (număr răspunsuri diferit, reconstruit la apply)."
               : "Deja sincronizată.",
         });
         continue;
      }

      if (apply) {
         report.updated += 1;
      } else {
         report.wouldUpdate += 1;
      }

      updates.push({
         ticketCode: official.ticketCode,
         bKey: official.label,
         positionInTicket: official.positionInTicket,
         questionId: siteQuestion.id,
         answerCountMismatch,
         payload,
      });

      rows.push({
         status: apply ? "updated" : "would_update",
         ticketCode: official.ticketCode,
         bKey: official.label,
         positionInTicket: official.positionInTicket,
         questionId: siteQuestion.id,
         message: answerCountMismatch
            ? "Se reconstruiesc răspunsurile după fișierul oficial."
            : "Se actualizează textele și răspunsul corect.",
      });
   }

   const officialTicketNos = new Set(
      officialItems.map((item) => Number(item.ticketNo)).filter(Boolean),
   );

   for (const siteQuestion of siteAll) {
      if (!officialTicketNos.has(Number(siteQuestion.ticketNo))) continue;
      if (matchedQuestionIds.has(Number(siteQuestion.id))) continue;

      report.notInOfficialFile += 1;
      rows.push({
         status: "not_in_official_file",
         ticketCode: siteQuestion.ticketCode,
         bKey: `poz. ${siteQuestion.positionInTicket}`,
         positionInTicket: siteQuestion.positionInTicket,
         questionId: siteQuestion.id,
         message: "Există în site, lipsește din fișierul oficial.",
      });
   }

   return {
      apply,
      report,
      rows,
      updates,
   };
}

export async function applyAutoTestAbUpdates(updates, updateQuestion, { onProgress } = {}) {
   const log = [];
   let step = 0;

   for (const update of safeList(updates)) {
      step += 1;
      onProgress?.({
         current: step,
         total: updates.length,
         label: `${update.ticketCode} · poz. ${update.positionInTicket}`,
      });

      try {
         await updateQuestion(update.questionId, update.payload);
         log.push({
            status: "ok",
            target: `${update.ticketCode} · poz.${update.positionInTicket} · #${update.questionId}`,
            message: "Actualizat.",
         });
      } catch (error) {
         log.push({
            status: "error",
            target: `${update.ticketCode} · poz.${update.positionInTicket} · #${update.questionId}`,
            message: error?.message || "Eroare la actualizare.",
         });
      }
   }

   return log;
}
